import { useMemo, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { FileSpreadsheet } from 'lucide-react'
import {
  getV1CurriculumAdminPlanRetrieveQueryKey,
  useV1CurriculumAdminPlanRetrieve,
  getV1MeRetrieveQueryOptions,
  useV1MeRetrieve,
  useV1QuizAdminCapabilities,
} from '@/api/client'
import { AnnualCalendarImportDialog } from '@/components/automatic-quiz/annual-calendar-import-dialog'
import { createAnnualCalendarImportApi } from '@/components/automatic-quiz/annual-calendar-import-api'
import { QuizCalendar } from '@/components/automatic-quiz/quiz-calendar'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { canAuthorQuiz, quizCapabilitiesOrClosed } from '@/components/quiz/quiz-capabilities'

export const Route = createFileRoute('/_panel/quizy-automatyczne/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: QuizCalendarPage,
})

function QuizCalendarPage() {
  const queryClient = useQueryClient()
  const { data: me } = useV1MeRetrieve()
  const isSuperadmin = me?.status === 200 && me.data.role === 'superadmin'
  const planQuery = useV1CurriculumAdminPlanRetrieve()
  const plan = planQuery.data?.status === 200 ? planQuery.data.data : undefined
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canEdit = isSuperadmin && canAuthorQuiz(capabilities)
  const [importOpen, setImportOpen] = useState(false)
  const [calendarInstance, setCalendarInstance] = useState(0)
  const importApi = useMemo(() => createAnnualCalendarImportApi(), [])

  const refreshCalendar = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getV1CurriculumAdminPlanRetrieveQueryKey() }),
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0]
          return (
            typeof key === 'string' &&
            key.startsWith('/api/v1/curriculum/admin/plan/versions/') &&
            key.includes('/calendar-slots/')
          )
        },
      }),
    ])
    setCalendarInstance((value) => value + 1)
  }

  return (
    <>
      <PageHeader
        title="Kalendarz quizów"
        actions={
          isSuperadmin ? (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/zadania/nowe">Quiz operatorski</Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEdit}
                title={!canEdit ? 'Edycja kalendarza jest wyłączona na serwerze.' : undefined}
                onClick={() => setImportOpen(true)}
              >
                <FileSpreadsheet data-icon="inline-start" /> Import XLSX
              </Button>
            </div>
          ) : undefined
        }
      />
      <AnnualCalendarImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        plan={plan}
        api={importApi}
        onImported={refreshCalendar}
      />
      <div className="p-6">
        <p className="mb-4 text-sm text-muted-foreground">
          Każdy termin wydaje dokładnie pytania zapisane w opublikowanym planie rocznym.
        </p>
        <QuizCalendar key={calendarInstance} canEdit={canEdit} />
      </div>
    </>
  )
}
